import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { Divider } from '@ui-kitten/components'
import { useDispatch } from 'react-redux'
import { NavigationContainer } from '@screens/Common/containers'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { useDispatchAsync } from '@utils'
import { CommonRefreshControl } from '@components'
import { Product, Subscription, UiPillType } from '@src/types.ts'
import {
  useEligibleCgmUpgradeProducts,
  useProducts,
  useSelectedAddonModal,
} from '@screens/Marketplace/utils/hooks.ts'
import { PurchasedProductSummary } from '@screens/Marketplace/components/NewMarketplace/PurchasedProductSummary.tsx'
import {
  ProductSummary,
  ProductSummaryProps,
} from '@screens/Marketplace/components/NewMarketplace/ProductSummary.tsx'
import { Text } from '@components/base'
import {
  cgmSteloDoubleImage,
  cgmSteloImage,
  marketplaceHandWPatchesImage,
  marketplaceIphoneWAppImage,
  marketplaceIphoneWAppImageVertical,
  marketplaceNNImage,
} from '@assets/images'
import { getMaxDiscountPromoDescriptor } from '@screens/Marketplace/utils/utils.ts'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType.ts'
import { shouldShowVideoCalls } from '@screens/Marketplace/components/NewMarketplace/utils.ts'
import { useActiveCoreSubscription } from '@src/hooks/useActiveSubscriptions.ts'
import {
  ProductExplanationScreenTypes,
  VIDEO_CALL_PRODUCT_DEFINITION,
} from '@screens/Marketplace/components/NewMarketplace/definitions.tsx'
import { SubscriptionsGroup } from '@screens/Marketplace/components/SubscriptionsGroup.tsx'
import { useActiveGroupedSubscriptions } from '@screens/Marketplace/components/NewMarketplace/hooks.ts'
import { useUpsellOffer } from '@src/hooks/useUpsellOffer'
import { PromoBadge } from '@src/components/PromoBadge'
import { usePreloadSteloAddonProducts } from '@screens/Marketplace/utils/hooks/useSteloCgmProducts'
import { PromoTag } from './NewMarketplace/PromoTag'

export const NewMarketplace = () => {
  const scrollViewRef = useRef<ScrollView>(null)
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const dispatchAsync = useDispatchAsync()
  const dispatch = useDispatch()
  const navigation = useNavigation()
  const onAddonSelected = useSelectedAddonModal()
  const { videoCallProductType } = useVideoCallProductType()

  const [refreshing, setRefreshing] = useState(false)

  const refreshingRef = useRef(refreshing)

  const { activeSubscriptions, oneTimeAddons, upcomingSubscriptionPhases, products } = useProducts()
  const { nsSubscriptions, uhcSubscriptions } = useActiveGroupedSubscriptions()

  usePreloadSteloAddonProducts(oneTimeAddons)

  const showUhcSubscriptions = uhcSubscriptions.length > 0

  const coreSubscription = useActiveCoreSubscription()
  const eligibleUpgradeProducts = coreSubscription?.primaryProduct.eligibleUpgradeProducts || []
  const cgmProducts = useEligibleCgmUpgradeProducts(products, eligibleUpgradeProducts)
  const upsellOffer = useUpsellOffer()

  const showCgmProducts = cgmProducts.length > 0
  const showByosProducts = eligibleUpgradeProducts.some((product) => product.ownSensor)
  const showVideoCalls = shouldShowVideoCalls(activeSubscriptions)
  const showUpsellOffer = !!upsellOffer

  const filteredCgmPromos = cgmProducts.flatMap((product) => {
    if (product.appliedPromo) {
      return [product.appliedPromo]
    }
    return []
  })
  const cgmAmountSaved = getMaxDiscountPromoDescriptor(filteredCgmPromos)
  const promoTag = cgmAmountSaved ? <PromoTag amountSaved={cgmAmountSaved} /> : null
  const cgmProductsImage = promoTag
    ? marketplaceIphoneWAppImageVertical
    : marketplaceIphoneWAppImage
  const cgmProductsImageStyle = promoTag ? { marginBottom: 32 } : { marginRight: 32 }
  const cgmProductsImageTagStyle = promoTag
    ? {
        marginBottom: 16,
        right: 12,
        bottom: 10,
        borderRadius: 16,
        width: 140,
      }
    : undefined

  const onVideoCallsPress = () => {
    navigation.navigate('ProductExplanation', {
      screen: ProductExplanationScreenTypes.InsuranceVideoCalls,
    })
  }

  const onCgmProgramsPress = () => {
    navigation.navigate('ProductExplanation', { screen: ProductExplanationScreenTypes.CgmProducts })
  }

  const onByosPress = () => {
    navigation.navigate('ProductExplanation', { screen: ProductExplanationScreenTypes.OwnSensor })
  }

  const onUpsellOfferPress = () => {
    if (!upsellOffer) {
      return
    }

    navigation.navigate('ProductExplanation', {
      upsellOffer,
    })
  }

  const onRefresh = useCallback(async () => {
    refreshingRef.current = true
    setRefreshing(true)

    try {
      await Promise.all([
        dispatchAsync({ type: 'users/fetch' }),
        dispatchAsync({ type: 'app/fetchProducts' }),
        dispatchAsync({ type: 'marketplace/fetchSubscriptionSchedules' }),
        dispatchAsync({ type: 'nutritionistHub/fetchPendingEhrAppointments' }),
        dispatchAsync({ type: 'nutritionistHub/fetchAppointmentInfo' }),
      ])
    } catch {
      // do nothing
    } finally {
      refreshingRef.current = false
      setRefreshing(false)
    }
  }, [dispatchAsync])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  useFocusEffect(
    useCallback(() => {
      if (refreshingRef.current) {
        return
      }

      dispatch({ type: 'users/fetch' })
    }, [dispatch]),
  )

  const getAddonImageProps = (
    addon: Product,
  ): Pick<ProductSummaryProps, 'image' | 'imageStyle'> => {
    if (addon.patch) {
      return { image: marketplaceHandWPatchesImage }
    }

    if (addon.cgm) {
      return {
        image: cgmSteloDoubleImage,
        imageStyle: { alignSelf: 'center', paddingHorizontal: 4 },
      }
    }

    return { image: undefined }
  }

  return (
    <NavigationContainer title="Programs & Add-ons">
      <ScrollView
        ref={scrollViewRef}
        style={styles.container}
        contentContainerStyle={{ paddingBottom: insets.bottom + 16 }}
        refreshControl={<CommonRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {showUhcSubscriptions && (
          <>
            <View style={styles.currentProductsContainer}>
              <SubscriptionsGroup
                title="SUBSCRIPTIONS"
                subscriptions={uhcSubscriptions as Subscription[]}
              />
            </View>
            <Divider style={styles.divider} />
          </>
        )}

        {nsSubscriptions.length > 0 && (
          <View style={styles.currentProductsContainer}>
            <SubscriptionsGroup
              title="NUTRISENSE SUBSCRIPTIONS"
              showTitle={showUhcSubscriptions}
              subscriptions={nsSubscriptions as Subscription[]}
            />
          </View>
        )}

        {upcomingSubscriptionPhases.length > 0 && (
          <View style={styles.currentProductsContainer}>
            {upcomingSubscriptionPhases.map((phase) => (
              <PurchasedProductSummary
                key={`phase-${phase.id}`}
                product={phase.primaryProduct!}
                pillLabel="Scheduled to start"
                pillStatus={UiPillType.Success}
              />
            ))}
          </View>
        )}

        {(showCgmProducts || showVideoCalls || showByosProducts || showUpsellOffer) && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.sectionContainer}>
              <Text type="title-3">Programs</Text>

              <View style={styles.productsContainer}>
                {showCgmProducts && (
                  <ProductSummary
                    title="Glucose Programs"
                    description="Optimize your health with glucose monitoring and dietitian support."
                    image={cgmProductsImage}
                    onPress={onCgmProgramsPress}
                    imageStyle={cgmProductsImageStyle}
                    imageTag={promoTag}
                    imageTagStyle={cgmProductsImageTagStyle}
                  />
                )}
                {showVideoCalls && (
                  <ProductSummary
                    {...VIDEO_CALL_PRODUCT_DEFINITION[videoCallProductType]}
                    image={marketplaceNNImage}
                    onPress={onVideoCallsPress}
                  />
                )}
                {showByosProducts && (
                  <ProductSummary
                    title="Bringing My Own CGM"
                    description="Activate your own sensor seamlessly with the Nutrisense app."
                    image={cgmSteloImage}
                    imageStyle={styles.steloImage}
                    onPress={onByosPress}
                  />
                )}
                {showUpsellOffer && (
                  <ProductSummary
                    title={upsellOffer.billingProductName}
                    description={upsellOffer.upgradeDescription}
                    onPress={onUpsellOfferPress}
                    imageTag={<PromoBadge badgeText={upsellOffer.badge} size={80} fontSize={18} />}
                    imageTagStyle={styles.promoBadge}
                  />
                )}
              </View>
            </View>
          </>
        )}

        {oneTimeAddons.length > 0 && (
          <>
            <Divider style={styles.divider} />

            <View style={styles.sectionContainer}>
              <Text type="title-3">Add-ons</Text>

              <View style={styles.productsContainer}>
                {oneTimeAddons.map((addon) => {
                  return (
                    <ProductSummary
                      key={addon.key}
                      title={addon.title}
                      description={addon.description || ''}
                      callout={addon.callout}
                      onPress={() => onAddonSelected(addon)}
                      price={addon.price}
                      showUnitPrice={addon.showUnitPrice}
                      units={addon.units}
                      isCgm={addon.cgm}
                      {...getAddonImageProps(addon)}
                    />
                  )
                })}
              </View>
            </View>
          </>
        )}
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  sectionContainer: {
    margin: 16,
  },
  currentProductsContainer: {
    marginHorizontal: 16,
  },
  productsContainer: {
    gap: 12,
    marginTop: 12,
  },
  divider: {
    backgroundColor: 'theme.surface.base2',
    height: 8,
  },
  promoBadge: {
    position: 'relative',
    alignSelf: 'center',
    marginRight: 16,
    marginLeft: 4,
  },
  steloImage: {
    alignSelf: 'center',
    width: 80,
    height: 88,
  },
})
